import {
  Breakpoints,
  Container,
  ContainerGutter,
  ContainerPadding,
  Layout,
  Richtext,
  styled,
  Text,
  CTA,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Close, InfoFilled } from '@volkswagen-onehub/icons-core';
import { CloseHandleV2, FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import React, { useRef } from 'react';
import { OffersFeatureAppConfig, Promotion } from 'src';
import { useFeatureAppConfig, useFeatureServices } from 'src/feature-app/hooks';
import { Promociones } from 'src/types';

interface PromotionLabelProps {
  offerPromotion: Promociones;
  index: number;
}

const Etiqueta = styled.div<{ promotion: Promotion; promotionName: String; index: number; ref: any }>`
  padding: 5px 12px;
  ${(props) => (props.promotionName === 'cuota-50' ? 'color: #FFFFFF' : '')};
  background-color: ${(props) =>
    props.promotionName === 'cuota-50'
      ? '#DC328A'
      : props.promotionName === 'gama-mas'
      ? '#F4E600'
      : props.promotionName === 'id3-pro-venta-online'
      ? '#37BF6D'
      : '#4cc7f4'};
  color: ${(props) =>
    props.promotionName === 'cuota-50' ? '#FFFFFF' : props.promotionName === 'id3-pro-venta-online' ? '#FFFFFF' : ''};
  border-radius: 4px;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.promotion ? 'pointer' : 'initial')};
  cursor: ${(props) => (props.promotion ? 'pointer' : 'initial')};

  & > div {
    margin: 0;
  }

  & svg {
    width: 16px;
    height: 16px;
    margin-left: 8px;
  }
`;

const FixedDiv = styled.div`
  display: 'block';
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border-radius: 15px;
  z-index: 2;
`;

const FixedDivDesktop = styled(FixedDiv)`
  position: absolute;
`

const ActionWrapper = styled.div`
  & button {
    min-height: 32px;
    padding: 4px 16px;
    min-width: 0px;
  }

  & div {
    top: 0;
  }
`;

const checkIfPromoExistsOnConfig = (config: OffersFeatureAppConfig, promotionName: string) =>
  config.promociones.hasOwnProperty(promotionName);

export const PromotionLabel = ({ offerPromotion, index }: PromotionLabelProps) => {
  const config = useFeatureAppConfig();
  const layerManager = useFeatureServices()['layer-manager'];
  const fixedDivRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef(null);

  const promotion = config.promociones?.[offerPromotion.name];

  const scrollTop = (element: HTMLDivElement) => {
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ block: 'start' });
      }, 0);
    }
  };

  const onCloseCallback = () => {
    if (fixedDivRef.current) {
      fixedDivRef.current.style.display = 'none';
    }
    if (topRef.current) {
      setTimeout(() => {
        topRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }, 800);
    }
  };

  const renderCerrarDiv = () => {
    return (
      <FixedDiv ref={fixedDivRef} className="fixed-div">
        <ActionWrapper>
          <CTA
            tag="button"
            emphasis="secondary"
            size="small"
            onClick={() => {
              layerRef.current.close();
              onCloseCallback();
            }}
            icon={<Close variant="small" />}
          >
            Cerrar
          </CTA>
        </ActionWrapper>
      </FixedDiv>
    );
  };

  const handlePromoClick = () => {
    if (promotion) {
      if (window.innerWidth < 559) {
        const layer = layerManager.openFocusLayer(
          renderPromo,
          {},
          { size: FocusLayerSizeV2.B, userCloseable: true, closeButton: renderCerrarDiv(), onClose: onCloseCallback }
        );
        layerRef.current = layer;
      } else {
        const layer = layerManager.openContentLayerV2(renderPromo, {}, {
          id: `Promotion-label-${offerPromotion.name}`, 
          closeButtonLabel: "Cerrar promotion label", 
          contentHeadline: "",
        })
        layerRef.current = layer;
      }
    }
  };

  const renderPromo = (state: any, close: CloseHandleV2<any, any>) => {
    // El CMS no desmonta el div del closeButton y lo tenemos que ocultar manualmente.
    if (fixedDivRef.current) {
      fixedDivRef.current.style.display = 'block';
    } else {
      document.getElementsByClassName('fixed-div')[0]?.style?.display = 'block';
    }

    return (
      <>
        <div
          ref={(e) => {
            scrollTop(e);
          }}
        />
        <div style={{ position: 'relative' }}>
          <Layout
            appearance={{
              [Breakpoints.default]: [
                { name: '.', columns: 2 },
                { name: 'a', columns: 20 },
                { name: '.', columns: 2 },
              ],
              [Breakpoints.b960]: [
                { name: '.', columns: 4 },
                { name: 'a', columns: 16 },
                { name: '.', columns: 4 },
              ],
            }}
          >
            <Container
              padding={{
                top: ContainerPadding.dynamic0200,
                bottom: { 0: ContainerPadding.dynamic0300, 560: ContainerPadding.dynamic0200 },
              }}
              gutter={ContainerGutter.static400}
              stretchContent
            >
              <Container>
                <Richtext markdown={promotion?.bodycopy} dangerouslyParseRawHTML />
              </Container>
              <Container>
                {promotion?.ctaTitle !== '' && (
                  <CTA emphasis="secondary" tag="a" href={promotion?.anchor}>
                    {promotion?.ctaTitle}
                  </CTA>
                )}
              </Container>
            </Container>
          </Layout>
        </div>
      </>
    );
  };

  const removeVehicleNameFromPromotion = (promotionName: string) => {
    let splittedPromoName: string[];

    if (promotionName.includes('gama-mas')) {
      splittedPromoName = promotionName.split('-');

      if (splittedPromoName.length > 2) {
        splittedPromoName.pop();
        return splittedPromoName.join('-');
      }
    }

    if (promotionName.includes('Gama MÁS')) {
      splittedPromoName = promotionName.split(' ');
      if (splittedPromoName.length > 2) {
        splittedPromoName.pop();
        return splittedPromoName.join(' ');
      }
    }

    return promotionName;
  };

  return (
    <Etiqueta
      ref={topRef}
      onClick={handlePromoClick}
      promotion={promotion}
      promotionName={removeVehicleNameFromPromotion(offerPromotion?.name)}
      index={index}
      className="etiqueta"
    >
      <Text appearance={TokenTextAppearance.copy100} color={TokenTextColor.inherit} bold>
        {removeVehicleNameFromPromotion(offerPromotion?.text)}
      </Text>
      {checkIfPromoExistsOnConfig(config, offerPromotion?.name) ? <InfoFilled /> : null}
    </Etiqueta>
  );
};
